import {cloneDeep, concat, filter, find, get, isEmpty, keys, set, sortBy} from 'lodash';
import arrayMove from 'array-move';
import {truncatedFilename} from 'actionCreators/uploadPdf';
import {updatePropertyPreferences} from 'api/propertyPreferences';
import {RESET} from './featureFlags';
import {isNZLocale} from 'lib/PropertyUtils';

const INCLUDE = 'pulp/reportSections/INCLUDE';
const ADD = 'pulp/reportSections/ADD';
const REMOVE = 'pulp/reportSections/REMOVE';
const UPDATE_VALUE = 'pulp/reportSections/UPDATE_VALUE';
const UPDATE_NOTIFICATION = 'pulp/reportSections/UPDATE_NOTIFICATION';
const SORT_SECTION = 'pulp/reportSections/SORT_SECTION';
const TOGGLE_FLOOR_PLAN = 'pulp/reportSections/TOGGLE_FLOOR_PLAN';

const firstSections = () => {
    return [
        {
            key: 'COVER_PAGE',
            uiLabel: 'Cover Page',
            included: true,
            sectionData: {
                title: 'Comparative Market Analysis'
            }
        },
        {
            key: 'COVER_LETTER',
            uiLabel: 'Cover Letter',
            included: true,
        },
        {
            key: 'TARGET_PROPERTY',
            uiLabel: 'Target Property',
            included: true,
            sectionData: {
                title: 'Your Property'
            }
        },
        {
            key: 'NZ_TITLES',
            uiLabel: 'Title Details',
            included: isNZLocale(),
            sectionData: {
                title: 'Title Details'
            }
        }
    ];
  }
;

const lastSections = () => {
  return [
    {
      key: 'MARKET_TRENDS',
      uiLabel: 'Market Trends',
      included: true,
      sectionData: {
        title: 'Market Trends'
      }
    },
    {
      key: 'SUBURB_PROFILE',
      uiLabel: 'Suburb Profile',
      included: true,
      defaultTitles: {
        demographic: 'Demographic',
        localSchools: 'Local Schools',
        recentMarketTrends: 'Recent Market Trends',
        longTermMarketTrends: 'Long Term Market Trends',
      },
      sectionData: {
        demographicTitle: 'Demographic',
        demographic_isEnabled: 'true',
        localSchoolsTitle: 'Local Schools',
        localSchools_isEnabled: 'true',
        recentMarketTrendsTitle: 'Recent Market Trends',
        recentMarketTrends_isEnabled: 'true',
        longTermMarketTrendsTitle: 'Long Term Market Trends',
        longTermMarketTrends_isEnabled: 'true',
        order: ['demographic', 'localSchools', 'recentMarketTrends', 'longTermMarketTrends'],
      },
    },
    {
      key: 'SUMMARY',
      uiLabel: 'Summary',
      included: true,
      sectionData: {
        title: 'Summary',
        appraisalPriceRange: {
          title: 'Appraisal price range',
          value: '',
          isEnabled: true,
        },
        description: {
          title: 'Notes from your agent',
          value: '',
          isEnabled: true,
        },
      }
    },
    {
      key: 'TESTIMONIALS',
      uiLabel: 'Testimonials',
      included: false,
      sectionData: {
        title: 'Testimonials',
      }
    },
    {
      key: 'DISCLAIMER',
      uiLabel: 'Disclaimer',
      included: true,
      sectionData: {
        title: 'DISCLAIMER'
      }
    },
  ];
};

const rentalSections = [
  {
    key: 'COMPARABLE_RENTALS_MAP',
    uiLabel: 'Comps Map: Rentals',
    included: null,
    sectionData: {
      title: 'Comps Map: Rentals',
    },
  },
  {
    key: 'COMPARABLE_RENTALS',
    uiLabel: 'Comparable Rentals',
    included: true,
    sectionData: {
      title: 'Comparable Rentals',
    },
  },
];

const cmaSections = [
  {
    key: 'COMPARABLE_MAP',
    uiLabel: 'Comp. Maps: All',
    included: null,
    sectionData: {
      title: 'Comparables Map: Sales & Listings',
    },
  },
  {
    key: 'COMPARABLE_SALES_MAP',
    uiLabel: 'Comps. Map: Sales',
    included: null,
    sectionData: {
      title: 'Comparables Map: Sales',
    },
  },
  {
    key: 'COMPARABLE_SALES',
    uiLabel: 'Comparable Sales',
    included: true,
    sectionData: {
      sortBy: 'distance_asc',
      title: 'Comparable Sales'
    }
  },
  {
    key: 'COMPARABLE_LISTINGS_MAP',
    uiLabel: 'Comps. Map: Listings',
    included: null,
    sectionData: {
      title: 'Comparables Map: Listings',
    },
  },
  {
    key: 'COMPARABLE_LISTINGS',
    uiLabel: 'Comparable Listings',
    included: true,
    sectionData: {
      sortBy: 'distance_asc',
      title: 'Comparable Listings'
    }
  },
];

export const initialState = () => {
  return {
    cma: [...cloneDeep(firstSections()), ...cmaSections, ...cloneDeep(lastSections())],
    rental: [...cloneDeep(firstSections()), ...rentalSections, ...cloneDeep(lastSections())],
  };
};

function addOrRemoveSection(state, reportType, name, enabled) {
  const data = state[reportType];
  const section = data.find((reportSection) => reportSection.key === name);
  if (!enabled && section) {
    data.splice(data.indexOf(section), 1);
  } else if (enabled && !section) {
    const newSection = initialState()[reportType].find(s => s.key === name);
    let index = initialState()[reportType].indexOf(newSection);
    data.splice(Math.min(index, data.length - 1), 0, newSection);
  }
}

function enabledSections(state) {
  let data = cloneDeep(state);
  enabledSectionsForReportType(data, 'cma');
  enabledSectionsForReportType(data, 'rental');
  return data;
}

function enabledSectionsForReportType(data, reportType) {
  addOrRemoveSection(data, reportType, 'COVER_LETTER', true);
  addOrRemoveSection(data, reportType, 'MARKET_TRENDS', false);
  addOrRemoveSection(data, reportType, 'SUBURB_PROFILE', true);
  if (reportType === 'cma') {
    addOrRemoveSection(data, reportType, 'COMPARABLE_MAP', true);
    addOrRemoveSection(data, reportType, 'COMPARABLE_SALES_MAP', true);
    addOrRemoveSection(data, reportType, 'COMPARABLE_LISTINGS_MAP', true);
  }
}
function handleSectionDataOrder(value = []) {
  const initialOrder = lastSections()[1].sectionData.order;
  return [...new Set([...value, ...initialOrder])];
}

export default function reducer(state = initialState(), action) {
  switch (action.type) {
    case RESET: {
      return enabledSections(state);
    }
    case INCLUDE: {
      let data = cloneDeep(state);
      let section = findSection(data, action.reportType, action.key, action.url);
      if (section) {
        section.included = action.included;
      }
      return data;
    }
    case UPDATE_VALUE: {
      let data = cloneDeep(state);
      let section = findSection(data, action.reportType, action.key, action.url);

      if (!section && action.url) {
        data = addSectionToState(state, addSectionToReport(action.reportType, action.url, action.dataKeyPath === 'filename' ? action.value : undefined));
        section = findSection(data, action.reportType, 'CUSTOM', action.url);
      }

      if (section) {
        let newValue = action.value;
        if (action.dataKeyPath === 'sectionData.order' && Array.isArray(action.value)) {
          newValue = handleSectionDataOrder(action.value);
        }
        set(section, action.dataKeyPath, newValue);
      }
      if (action.key === 'CUSTOM') {
        renderDefaultLabelIfNeeded(section);
      } else {
        renderDefaultTitlesIfNeeded(section, action.reportType, action.key, 'sectionData');
      }
      return data;
    }
    case ADD: {
      return addSectionToState(state, action);
    }
    case REMOVE: {
      return removeSectionFromState(state, action);
    }
    case UPDATE_NOTIFICATION: {
      return {
        ...state,
        showNotification: action.flag,
      };
    }
    case SORT_SECTION: {
      if (action.oldIndex === action.newIndex) {
        return state;
      } else {
        let data = cloneDeep(state);
        let included = sortedSections(data[action.reportType]);

        reindexedSections(included, action.oldIndex, action.newIndex);

        return data;
      }
    }
    case TOGGLE_FLOOR_PLAN: {
      return addOrRemoveFloorPlanSectionFromState(action, state);
    }
    default:
      return state;
  }
}

function reindexOnSectionRemove(sections, index) {
  sections.forEach(section => {
    if (typeof section.index !== 'undefined') {
      if (section.index > index) {
        section.index = section.index - 1;
      }
    }
  });
}

export function reindexedSections(sections, oldIndex, newIndex) {
  const _sections = arrayMove(sections, oldIndex, newIndex);
  _sections.forEach((s, idx) => {
    s.index = idx;
  });
  return _sections;
}

export const checkIfSectionCanBeUpdated = (section, oldIndex, newIndex) => {
  if (typeof section.index !== 'undefined') {
    const oldIndexAhead =
      oldIndex > newIndex &&
      section.index < oldIndex &&
      section.index >= newIndex;
    const oldIndexBehind =
      oldIndex < newIndex &&
      section.index > oldIndex &&
      section.index <= newIndex;

    return oldIndexAhead || oldIndexBehind;
  }
  return false;
};

export function sortedSections(sections) {
  let _sections = sections;
  if (!isNZLocale()) {
    _sections = _sections.filter(s => !s.key.toLowerCase().startsWith('nz'));
  }
  let results = filter(_sections, (is) => (typeof is.index === 'undefined'));
  let indexedItems = sortBy(filter(_sections, (is) => typeof is.index !== 'undefined'), ['index']);
  indexedItems.forEach(item => {
    results.splice(item.index, 0, item);
  });
  return results;
}

function removeFloorPlanSectionFromState(action, state, section) {
  reindexOnSectionRemove(state[action.reportType], section.index || state[action.reportType].indexOf(section));
  state[action.reportType] = filter(state[action.reportType], (reportSection) => reportSection.key !== 'FLOOR_PLAN');
  return state;
}

function addFloorPlanSectionToState(state, action) {
  if (!isNZLocale()) {
    let section = {
      key: 'FLOOR_PLAN',
      uiLabel: 'Floor Plan',
      included: true,
      sectionData: {
        title: 'Floor Plan',
        floorPlanUris: action.floorPlanUris,
      },
    };
    let targetPropertyIndex = state[action.reportType]?.findIndex((s) => s.key === 'TARGET_PROPERTY');
    if (targetPropertyIndex > -1) {
      state[action.reportType].splice(targetPropertyIndex + 1, 0, section);
    } else {
      state[action.reportType] = (state[action.reportType] || []).concat(section);
    }
  }
  return state;
}

function addOrRemoveFloorPlanSectionFromState(action, state) {
  const newState = cloneDeep(state);
  let sections = newState[action.reportType];
  let section = find(sections, (s = {}) => s?.key === 'FLOOR_PLAN');
  if (section) {
    if (isEmpty(action.floorPlanUris)) {
      return removeFloorPlanSectionFromState(action, newState, section);
    }
    section.sectionData.floorPlanUris = action.floorPlanUris;
    return newState;
  }
  if (!isEmpty(action.floorPlanUris)) {
    return addFloorPlanSectionToState(newState, action);
  }
  return newState;
}

function addSectionToState(state, action) {
  let data = {...state};
  data[action.reportType] = concat(data[action.reportType], action.payload);
  return data;
}

function removeSectionFromState(state, action) {
  let data = {
    ...state,
  };
  const sections = data[action.reportType];
  const section = sections.find(reportSection => get(reportSection, 'url') === action.payload.url);
  reindexOnSectionRemove(sections, section.index || sections.indexOf(section));
  data[action.reportType] = filter(data[action.reportType], (reportSection) => get(reportSection, 'url') !== action.payload.url);
  return data;
}


function renderDefaultLabelIfNeeded(section) {
  if (!section.uiLabel) {
    section.uiLabel = truncatedFilename(section.filename);
  }
}

function renderDefaultTitlesIfNeeded(section, reportType, sectionKey, keyPath) {
  let defaultSection = find(initialState()[reportType], (s) => s.key === sectionKey);
  if (!get(section, `${keyPath}.title`)) {
    let defaultTitle = get(defaultSection, `${keyPath}.title`);
    set(section, `${keyPath}.title`, defaultTitle);
  }

  keys(get(defaultSection, keyPath)).forEach(subKey => {
    let subKeyPath = `${keyPath}.${subKey}`;
    if (get(defaultSection, `${subKeyPath}.title`)) {
      renderDefaultTitlesIfNeeded(section, reportType, sectionKey, subKeyPath);
    }
  });
}

export function includeSectionInReport(reportType, key, url) {
  return {
    type: INCLUDE,
    key,
    url,
    reportType,
    included: true,
  };
}

export function excludeSectionFromReport(reportType, key, url) {
  return {
    type: INCLUDE,
    key,
    url,
    reportType,
    included: false,
  };
}

export function addSectionToReport(reportType, url, filename) {
  return {
    type: ADD,
    reportType,
    payload: {
      key: 'CUSTOM',
      filename,
      uiLabel: truncatedFilename(filename),
      included: true,
      url,
    }
  };
}

export function removeSectionFromReport(reportType, url) {
  return {
    type: REMOVE,
    reportType,
    payload: {
      url
    }
  };
}

export function updateSectionValue({value, reportType, key, url, dataKeyPath}, savePreference = false) {
  return (dispatch, getState) => {
    if (savePreference) {
      const {propertyDetail: {propertyId}} = getState();
      updatePropertyPreferences(propertyId, {[`${key.toLowerCase()}.${dataKeyPath}`]: value});
    }

    dispatch({
      type: UPDATE_VALUE,
      value,
      reportType,
      key,
      url,
      dataKeyPath,
    });
  };
}

export function findSection(reportSections, reportType, key, url) {
  return findReportTypeSection(reportSections[reportType], key, url);
}

export function findReportTypeSection(sections, key, url) {
  return find(sections, (s) => s.key === key && s.url === url);
}

export function sortSectionInReport(reportType, oldIndex, newIndex) {
  return {
    type: SORT_SECTION,
    reportType,
    oldIndex,
    newIndex,
  };
}

export const checkIncluded = ({key, included} = {included: false}, comparables = {}, floorPlanUris = []) => {
  if (included !== null) {
    return included;
  }

  if (key === 'COMPARABLE_MAP') {
    return comparables.comparableSales.selected.length > 0 &&
      comparables.comparableListings.selected.length > 0;
  }

  if (key === 'FLOOR_PLAN') {
    return !isEmpty(floorPlanUris);
  }

  const keyToType = {
    COMPARABLE_SALES_MAP: 'comparableSales',
    COMPARABLE_LISTINGS_MAP: 'comparableListings',
    COMPARABLE_RENTALS_MAP: 'comparableRentals',
  };
  return comparables[keyToType[key]].selected.length > 0;
};


export function toggleFloorPlan(reportType, floorPlanUris) {
  return {
    type: TOGGLE_FLOOR_PLAN,
    reportType: reportType,
    floorPlanUris: floorPlanUris
  };
}
